/**
 * Async ops watcher — polls `/widget/operations/:id` for every pending async
 * operation in the store (image generation, equip, hatch) and resolves the
 * store entry when the backend reports completion.
 *
 * Without this loop every widget that can show async content — evolution
 * ceremony, badge icon generation, marketplace equip — would have to poll
 * the same endpoint on its own. Centralizing avoids N identical fetch loops
 * when more than one widget cares about the same operation.
 *
 * Cadence: 2s while anything is pending, stopped otherwise. Cheap — even
 * under worst-case (5 simultaneous pending ops, 30s until resolution) that's
 * 75 requests spread over 30 seconds. Far below any reasonable rate limit.
 */
import type { WorkspaceStore } from './store';

export interface AsyncOpsWatcherOptions {
  store: WorkspaceStore;
  apiBaseUrl: string;
  token: string;
  /** Poll cadence in ms while any op is pending. */
  interval?: number;
  /** Max wall-clock wait per op before we give up and mark it resolved. */
  timeoutMs?: number;
  /**
   * Called right after an op transitions out of pending. Used by the loader
   * to nudge smart-poll so buddy/badges reflect the async result without
   * waiting for the next scheduled tick — important for evolution/hatch,
   * where the image URL on `buddy` only updates once generation finishes.
   */
  onResolve?: (opId: string, result: Record<string, unknown> | null) => void;
}

interface OperationStatusResponse {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  result_payload: Record<string, unknown> | null;
  error_payload: Record<string, unknown> | null;
  updated_at: string;
}

export function startAsyncOpsWatcher(opts: AsyncOpsWatcherOptions): () => void {
  const interval = opts.interval ?? 2_000;
  const timeoutMs = opts.timeoutMs ?? 120_000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let stopped = false;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const tick = async () => {
    if (stopped) return;
    running = true;
    try {
      const pending = opts.store.getState().pendingOps;
      if (pending.length === 0) {
        running = false;
        return;
      }

      // Fetch each pending op in parallel. Failures are isolated — a single
      // 404 shouldn't wedge the whole batch.
      const results = await Promise.allSettled(
        pending.map((op) =>
          fetch(`${opts.apiBaseUrl}/widget/operations/${op.id}`, {
            headers: { Authorization: `Bearer ${opts.token}` },
          }).then((res) => {
            if (!res.ok) throw new Error(`op ${op.id} -> ${res.status}`);
            return res.json() as Promise<OperationStatusResponse>;
          }),
        ),
      );

      for (let i = 0; i < pending.length; i += 1) {
        const op = pending[i];
        const settled = results[i];
        if (!op || !settled) continue;

        // Timeout protection: if the op has been around too long we resolve
        // it locally so widgets stop showing a perpetual shimmer. The backend
        // record stays authoritative — a subsequent /widget/state refresh
        // will overwrite whatever we guessed.
        if (Date.now() - op.startedAt > timeoutMs) {
          opts.store.resolvePending(op.id);
          opts.onResolve?.(op.id, null);
          continue;
        }

        if (settled.status !== 'fulfilled') continue;
        const data = settled.value;
        if (
          data.status === 'completed' ||
          data.status === 'failed' ||
          data.status === 'cancelled'
        ) {
          opts.store.resolvePending(op.id, data.result_payload ?? undefined);
          opts.onResolve?.(op.id, data.result_payload);
        }
      }
    } finally {
      running = false;
      if (!stopped && opts.store.getState().pendingOps.length > 0) {
        timer = setTimeout(tick, interval);
      }
    }
  };

  const unsubscribe = opts.store.subscribe(() => {
    if (stopped) return;
    // Only arm a new timer when we transition from "idle" to "has pending".
    if (!running && timer === null && opts.store.getState().pendingOps.length > 0) {
      timer = setTimeout(tick, interval);
    }
  });

  // If the store already has pending ops at watcher-start, kick immediately.
  if (opts.store.getState().pendingOps.length > 0) {
    timer = setTimeout(tick, 0);
  }

  return () => {
    stopped = true;
    clear();
    unsubscribe();
  };
}
